import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface IPException {
  id: number;
  ip_address: string;
  name: string;
  description: string;
  created_at: string;
  is_active: boolean;
  created_by_username?: string;
}

const ipExceptionSchema = z.object({
  ip_address: z.string().min(7, 'Введите корректный IP адрес'),
  name: z.string().min(1, 'Название обязательно'),
  description: z.string().optional(),
});

const editIpExceptionSchema = z.object({
  name: z.string().min(1, 'Название обязательно'),
  description: z.string().optional(),
  is_active: z.boolean(),
});

export function IPExceptions() {
  const [exceptions, setExceptions] = useState<IPException[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedException, setSelectedException] = useState<IPException | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const addForm = useForm<z.infer<typeof ipExceptionSchema>>({
    resolver: zodResolver(ipExceptionSchema),
    defaultValues: {
      ip_address: '',
      name: '',
      description: '',
    },
  });

  const editForm = useForm<z.infer<typeof editIpExceptionSchema>>({
    resolver: zodResolver(editIpExceptionSchema),
    defaultValues: {
      name: '',
      description: '',
      is_active: true,
    },
  });

  useEffect(() => {
    fetchIPExceptions();
  }, []);

  const fetchIPExceptions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/admin/ip_exceptions', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setExceptions(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching IP exceptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddException = async (values: z.infer<typeof ipExceptionSchema>) => {
    try {
      const formData = new FormData();
      formData.append('ip_address', values.ip_address);
      formData.append('name', values.name);
      if (values.description) {
        formData.append('description', values.description);
      }

      const response = await fetch('/api/auth/admin/ip_exceptions', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setIsAddDialogOpen(false);
          addForm.reset();
          fetchIPExceptions();
        }
      }
    } catch (error) {
      console.error('Error adding IP exception:', error);
    }
  };

  const handleEditException = (exception: IPException) => {
    setSelectedException(exception);
    editForm.reset({
      name: exception.name,
      description: exception.description,
      is_active: exception.is_active,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateException = async (values: z.infer<typeof editIpExceptionSchema>) => {
    if (!selectedException) return;

    try {
      const formData = new FormData();
      formData.append('name', values.name);
      if (values.description) {
        formData.append('description', values.description);
      }
      formData.append('is_active', values.is_active.toString());

      const response = await fetch(`/admin/api/ip_exceptions/${selectedException.id}`, {
        method: 'PUT',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setIsEditDialogOpen(false);
          fetchIPExceptions();
        }
      }
    } catch (error) {
      console.error('Error updating IP exception:', error);
    }
  };

  const handleDeleteException = async (exceptionId: number, ipAddress: string) => {
    if (!confirm(`Удалить IP исключение для ${ipAddress}?`)) return;

    try {
      const response = await fetch(`/api/auth/admin/ip_exceptions/${exceptionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchIPExceptions();
      }
    } catch (error) {
      console.error('Error deleting IP exception:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>IP исключения</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Загрузка IP исключений...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>IP исключения</CardTitle>
        <CardDescription>
          Управление IP адресами для обхода Turnstile проверки
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Кнопка добавления */}
          <div className="flex justify-end">
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>Добавить IP исключение</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Добавить IP исключение</DialogTitle>
                  <DialogDescription>
                    Добавить новый IP адрес в список исключений
                  </DialogDescription>
                </DialogHeader>
                <Form {...addForm}>
                  <form onSubmit={addForm.handleSubmit(handleAddException)} className="space-y-4">
                    <FormField
                      control={addForm.control}
                      name="ip_address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>IP адрес</FormLabel>
                          <FormControl>
                            <Input placeholder="192.168.1.1" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={addForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Название</FormLabel>
                          <FormControl>
                            <Input placeholder="Офисный IP" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={addForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Описание (необязательно)</FormLabel>
                          <FormControl>
                            <Input placeholder="Дополнительная информация" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex justify-end space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddDialogOpen(false)}
                      >
                        Отмена
                      </Button>
                      <Button type="submit">
                        Добавить
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Список исключений */}
          <div className="space-y-2">
            {exceptions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Нет IP исключений
              </p>
            ) : (
              exceptions.map((exception) => (
                <div key={exception.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-medium">{exception.ip_address}</span>
                      <span className="font-medium">{exception.name}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        exception.is_active 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {exception.is_active ? 'Активно' : 'Неактивно'}
                      </span>
                    </div>
                    {exception.description && (
                      <div className="text-sm text-muted-foreground">
                        {exception.description}
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground">
                      Создано: {new Date(exception.created_at).toLocaleString()}
                      {exception.created_by_username && ` • ${exception.created_by_username}`}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditException(exception)}
                    >
                      Редактировать
                    </Button>
                    {exception.ip_address !== '217.156.122.183' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteException(exception.id, exception.ip_address)}
                      >
                        Удалить
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Edit Exception Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Редактировать IP исключение</DialogTitle>
                <DialogDescription>
                  Изменение данных IP исключения {selectedException?.ip_address}
                </DialogDescription>
              </DialogHeader>
              <Form {...editForm}>
                <form onSubmit={editForm.handleSubmit(handleUpdateException)} className="space-y-4">
                  <FormField
                    control={editForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Название</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Описание</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="h-4 w-4"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Активно</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditDialogOpen(false)}
                    >
                      Отмена
                    </Button>
                    <Button type="submit">
                      Сохранить
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}
